import { FC, useState, useMemo } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import { OP } from '../core/operationPermissions'
import { useOperationsAccess } from '../core/useOperationsAccess'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import PortSelect from '../../../shared/components/PortSelect'

type InportConsumptionRecord = {
  id: number
  vessel: string
  reportDate: string
  port: string
  fromDate: string
  fromTime: string
  toDate: string
  toTime: string
  portStayHours: string
}

const mockInportConsumptionData: InportConsumptionRecord[] = [
  {
    id: 1,
    vessel: 'Troy',
    reportDate: '07-Sep-2025',
    port: 'Mundra',
    fromDate: '31-Aug-2025',
    fromTime: '05:18',
    toDate: '07-Sep-2025',
    toTime: '19:40',
    portStayHours: '182 Hrs 22 Min'
  },
  {
    id: 2,
    vessel: 'Troy',
    reportDate: '19-Aug-2025',
    port: 'Suez Canal',
    fromDate: '19-Aug-2025',
    fromTime: '00:00',
    toDate: '19-Aug-2025',
    toTime: '00:00',
    portStayHours: '00 Hrs 00 Min'
  },
  {
    id: 3,
    vessel: 'Troy',
    reportDate: '16-Aug-2025',
    port: 'Canakkale',
    fromDate: '12-Aug-2025',
    fromTime: '17:00',
    toDate: '16-Aug-2025',
    toTime: '00:00',
    portStayHours: '79 Hrs 00 Min'
  },
  {
    id: 4,
    vessel: 'Troy',
    reportDate: '14-Aug-2025',
    port: 'Istanbul',
    fromDate: '12-Aug-2025',
    fromTime: '17:00',
    toDate: '14-Aug-2025',
    toTime: '00:00',
    portStayHours: '31 Hrs 00 Min'
  }
]

interface AddInportConsumptionModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: (data: Omit<InportConsumptionRecord, 'id'>, saveAndAddNew: boolean) => void
}

const AddInportConsumptionModal: FC<AddInportConsumptionModalProps> = ({ visible, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    vessel: '',
    reportDate: '',
    port: '',
    fromDate: '',
    fromTime: '',
    toDate: '',
    toTime: '',
    portStayHours: ''
  })
  const [errors, setErrors] = useState<{[key: string]: string}>({})

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {}
    
    if (!formData.vessel.trim()) newErrors.vessel = 'Vessel is required'
    if (!formData.reportDate.trim()) newErrors.reportDate = 'Report Date is required'
    if (!formData.port.trim()) newErrors.port = 'Port is required'
    if (!formData.fromDate.trim()) newErrors.fromDate = 'From Date is required'
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (saveAndAddNew: boolean = false) => {
    if (!validateForm()) return

    onSubmit(formData, saveAndAddNew)
    
    if (saveAndAddNew) {
      setFormData({
        vessel: '',
        reportDate: '',
        port: '',
        fromDate: '',
        fromTime: '',
        toDate: '',
        toTime: '',
        portStayHours: ''
      })
      setErrors({})
    } else {
      setFormData({
        vessel: '',
        reportDate: '',
        port: '',
        fromDate: '',
        fromTime: '',
        toDate: '',
        toTime: '',
        portStayHours: ''
      })
      setErrors({})
      onClose()
    }
  }

  const handleClose = () => {
    setFormData({
      vessel: '',
      reportDate: '',
      port: '',
      fromDate: '',
      fromTime: '',
      toDate: '',
      toTime: '',
      portStayHours: ''
    })
    setErrors({})
    onClose()
  }

  if (!visible) return null

  return (
    <div
      className="modal fade show d-flex align-items-center justify-content-center"
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className='modal-dialog modal-xl modal-dialog-centered' role='document'>
        <div className='modal-content bg-white' style={{ color: '#181C32' }}>
          <div className='modal-header'>
            <h5 className='modal-title'>Add New In-Port Consumption Report</h5>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>

          <div className='modal-body'>
            <div className='row g-3'>
              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Vessel</label>
                <SearchableSelect
                  className={`form-select ${errors.vessel ? 'is-invalid' : ''}`}
                  name='vessel'
                  value={formData.vessel}
                  onChange={handleInputChange}
                >
                  <option value=''>Select Vessel</option>
                  <option value='Troy'>Troy</option>
                  <option value='Atlas'>Atlas</option>
                  <option value='Neptune'>Neptune</option>
                </SearchableSelect>
                {errors.vessel && <div className='invalid-feedback'>{errors.vessel}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Report Date</label>
                <input
                  type='date'
                  className={`form-control ${errors.reportDate ? 'is-invalid' : ''}`}
                  name='reportDate'
                  value={formData.reportDate}
                  onChange={handleInputChange}
                  style={{ color: '#000' }}
                />
                {errors.reportDate && <div className='invalid-feedback'>{errors.reportDate}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Port</label>
                <PortSelect
                  name='port'
                  value={formData.port}
                  onChange={handleInputChange}
                  placeholder='Select or search port...'
                />
                {errors.port && <div className='invalid-feedback d-block'>{errors.port}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>From Date</label>
                <input
                  type='date'
                  className={`form-control ${errors.fromDate ? 'is-invalid' : ''}`}
                  name='fromDate'
                  value={formData.fromDate}
                  onChange={handleInputChange}
                  style={{ color: '#000' }}
                />
                {errors.fromDate && <div className='invalid-feedback'>{errors.fromDate}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>From Time</label>
                <input
                  type='time'
                  className='form-control'
                  name='fromTime'
                  value={formData.fromTime}
                  onChange={handleInputChange}
                  style={{ color: '#000' }}
                />
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>To Date</label>
                <input
                  type='date'
                  className='form-control'
                  name='toDate'
                  value={formData.toDate}
                  onChange={handleInputChange}
                  style={{ color: '#000' }}
                />
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>To Time</label>
                <input
                  type='time'
                  className='form-control'
                  name='toTime'
                  value={formData.toTime}
                  onChange={handleInputChange}
                  style={{ color: '#000' }}
                />
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Port Stay Hours</label>
                <input
                  type='text'
                  className='form-control'
                  name='portStayHours'
                  value={formData.portStayHours}
                  onChange={handleInputChange}
                  placeholder='e.g., 182 Hrs 22 Min'
                  style={{ color: '#000' }}
                />
              </div>
            </div>
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-light btn-sm' onClick={handleClose}>
              Cancel
            </button>
            <button 
              type='button' 
              className='btn btn-secondary btn-sm me-2'
              onClick={() => handleSubmit(true)}
            >
              Save & Add New
            </button>
            <button 
              type='button' 
              className='btn btn_primary'
              onClick={() => handleSubmit(false)}
            >
              Save & Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const InportConsumptionReport: FC = () => {
  const { opCan, permissionsLoaded } = useOperationsAccess()
  const canView = opCan(OP.READ_IN_PORT_CONSUMPTION_REPORT)
  const canAdd = opCan(OP.UPDATE_IN_PORT_CONSUMPTION_REPORT)

  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [reportData, setReportData] = useState<InportConsumptionRecord[]>(mockInportConsumptionData)
  const [sortConfig, setSortConfig] = useState<{
    key: keyof InportConsumptionRecord | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' })
  const [isModalVisible, setIsModalVisible] = useState(false)

  // Filters
  const [filters, setFilters] = useState({
    vessel: '',
    port: '',
    reportFromDate: '',
    reportToDate: ''
  })

  const handleFilterChange = (name: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleClearFilters = () => {
    setFilters({
      vessel: '',
      port: '',
      reportFromDate: '',
      reportToDate: ''
    })
  }

  const filteredData = useMemo(() => {
    return reportData.filter(record => {
      const matchesSearch = searchTerm === '' || 
        record.vessel.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.port.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.reportDate.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesVessel = filters.vessel === '' || record.vessel === filters.vessel
      const matchesPort = filters.port === '' || record.port.toLowerCase().includes(filters.port.toLowerCase())
      
      return matchesSearch && matchesVessel && matchesPort
    })
  }, [searchTerm, reportData, filters])

  const sortedData = useMemo(() => {
    let sortedRecords = [...filteredData]

    if (sortConfig.key !== null) {
      sortedRecords.sort((a, b) => {
        const aVal = String(a[sortConfig.key!] || '').toLowerCase()
        const bVal = String(b[sortConfig.key!] || '').toLowerCase()

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return sortedRecords
  }, [filteredData, sortConfig])

  const handleSort = (key: keyof InportConsumptionRecord) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const indexOfLastRecord = currentPage * rowsPerPage
  const indexOfFirstRecord = indexOfLastRecord - rowsPerPage
  const currentRecords = sortedData.slice(indexOfFirstRecord, indexOfLastRecord)
  const totalPages = Math.ceil(sortedData.length / rowsPerPage)

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value))
    setCurrentPage(1)
  }

  const handleAddReport = (data: Omit<InportConsumptionRecord, 'id'>, saveAndAddNew: boolean) => {
    if (!canAdd) return
    const newRecord: InportConsumptionRecord = {
      ...data,
      id: Math.max(...reportData.map(m => m.id)) + 1,
    }
    setReportData(prev => [...prev, newRecord])
  }

  if (permissionsLoaded && !canView) {
    return (
      <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
        <div className='alert alert-warning m-5'>You do not have permission to perform this action.</div>
      </div>
    )
  }

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>

            <div className='card-header border-0 pt-6 d-flex flex-column flex-lg-row justify-content-between bg-white gap-3'>
              <div>
                <h3 className='card-label text-dark fw-bold'>In-Port Consumption Report</h3>
                <p className='text-muted small mb-0 mt-1'>
                  Deferred: mock list only; no server API, persistence, or scope rules on this page yet. UI gates
                  use report permissions for when a backend is connected.
                </p>
              </div>
              <div className='card-toolbar'>
                {canAdd && (
                <button
                  type='button'
                  className='btn btn_primary'
                  onClick={() => setIsModalVisible(true)}
                >
                  <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                  Add New In-Port Consumption Report
                </button>
                )}
              </div>
            </div>

            <div className='card-body py-4 bg-white border-top'>
              <div className='row gx-3 gy-3 mb-4'>
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>
                    Vessel
                  </label>
                  <SearchableSelect
                    className='form-select'
                    style={{
                      border: '1px solid #E4E6EF',
                      borderRadius: '6px',
                      fontSize: '14px',
                      padding: '8px 12px',
                      color: '#5E6278',
                    }}
                    value={filters.vessel}
                    onChange={(e) => handleFilterChange('vessel', e.target.value)}
                  >
                    <option value=''>9 items checked</option>
                    <option value='Troy'>Troy</option>
                    <option value='Atlas'>Atlas</option>
                    <option value='Neptune'>Neptune</option>
                  </SearchableSelect>
                </div>

                 <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>
                    Port
                  </label>
                  <SearchableSelect
                    className='form-select'
                    style={{
                      border: '1px solid #E4E6EF',
                      borderRadius: '6px',
                      fontSize: '14px',
                      padding: '8px 12px',
                      color: '#5E6278',
                    }}
                    value={filters.port}
                    onChange={(e) => handleFilterChange('port', e.target.value)}
                  >
                    <option value=''>Select Port</option>
                    <option value='Mundra'>Mundra</option>
                    <option value='Suez Canal'>Suez Canal</option>
                    <option value='Canakkale'>Canakkale</option>
                    <option value='Istanbul'>Istanbul</option>
                  </SearchableSelect>
                </div>

                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>
                    Report From Date
                  </label>
                  <input
                    type='date'
                    className='form-control'
                    style={{
                      border: '1px solid #E4E6EF',
                      borderRadius: '6px',
                      fontSize: '14px',
                      padding: '8px 12px',
                      color: '#5E6278',
                    }}
                    value={filters.reportFromDate}
                    onChange={(e) => handleFilterChange('reportFromDate', e.target.value)}
                  />
                </div>

                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>
                    Report To Date
                  </label>
                  <input
                    type='date'
                    className='form-control'
                    style={{
                      border: '1px solid #E4E6EF',
                      borderRadius: '6px',
                      fontSize: '14px',
                      padding: '8px 12px',
                      color: '#5E6278',
                    }}
                    value={filters.reportToDate}
                    onChange={(e) => handleFilterChange('reportToDate', e.target.value)}
                  />
                </div>

                <div className='col-md-4 d-flex align-items-end gap-2'>
                  <button
                    type='button'
                    className='btn btn_primary'
                  >
                    Search
                  </button>

                  <button
                    type='button'
                    className='btn btn-light'
                    style={{
                      fontSize: '14px',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      height: 'fit-content'
                    }}
                    onClick={handleClearFilters}
                  >
                    Clear
                  </button>

                    
                </div>
              </div>

              {/* Departure Reports Table */}
              <div className='mb-8'>
                <h4 className='fw-bold text-primary mb-4'>Departure Reports</h4>
                <div className='report-table table-responsive'>
                  <div style={{ overflowX: 'auto' }}>
                    <table className='table align-middle'>
                    <thead className='table-header text-start'>
                    <tr>
                        <th onClick={() => handleSort('vessel')} className='cursor-pointer align-middle'>
                        <div className='d-flex align-items-center'>
                            Vessel
                            <KTSVG path={`/media/map/sort-col-${ sortConfig.key === 'vessel' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey' }.svg`} className='svg-icon ms-2 custom-sort-icon'/>
                        </div>
                        </th>
                        <th onClick={() => handleSort('reportDate')} className='cursor-pointer align-middle'>
                        <div className='d-flex align-items-center'>
                            Report Date
                            <KTSVG path={`/media/map/sort-col-${ sortConfig.key === 'reportDate' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey' }.svg`} className='svg-icon ms-2 custom-sort-icon'/>
                        </div>
                        </th>
                        <th onClick={() => handleSort('port')} className='cursor-pointer align-middle'>
                        <div className='d-flex align-items-center'>
                            Port
                            <KTSVG path={`/media/map/sort-col-${ sortConfig.key === 'port' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey' }.svg`} className='svg-icon ms-2 custom-sort-icon'/>
                        </div>
                        </th>
                        <th className='align-middle text-center'>From Date</th>
                        <th className='align-middle text-center'>To Date</th>
                        <th className='align-middle text-center'>Port Stay Hours</th>
                    </tr>
                    </thead>
                      <tbody className='table-body text-start'>
                        {currentRecords.length === 0 ? (
                          <tr>
                            <td colSpan={6} className='text-center text-muted py-5'>
                              No departure reports found for the selected criteria.
                            </td>
                          </tr>
                        ) : (
                        currentRecords.map((record) => (
                        <tr key={record.id}>
                            <td className='text-dark fw-semibold fs-6'>{record.vessel}</td>
                            <td className='text-dark fw-semibold fs-6'>{record.reportDate}</td>
                            <td className='text-dark fs-6'>
                            <div className='d-flex align-items-center gap-2'>
                                <img 
                                src='/media/flags/india.svg' 
                                alt='flag' 
                                style={{ width: '20px', height: '15px' }}
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none'
                                }}
                                />
                                {record.port}
                            </div>
                            </td>
                            <td className='text-dark fs-6 text-center'>
                            <div>{record.fromDate}</div>
                            <div className='text-muted'>{record.fromTime}</div>
                            </td>
                            <td className='text-dark fs-6 text-center'>
                            <div>{record.toDate}</div>
                            <div className='text-muted'>{record.toTime}</div>
                            </td>
                            <td className='text-dark fs-6 text-center'>{record.portStayHours}</td>
                        </tr>
                        ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div 
                    className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                    style={{
                      position: 'static',
                      bottom: 0,
                      backgroundColor: '#fff',
                      zIndex: 10,
                      borderTop: '1px solid #dee2e6',
                      marginTop: 'auto'
                    }}
                  >
                    <div className='d-flex align-items-center'>
                      <span className='text-muted me-2'>Rows per page</span>
                      <SearchableSelect
                        className='form-select'
                        style={{
                          borderRadius: '20px',
                          width: '70px',
                          border: '1px solid #dee2e6',
                          fontSize: '14px',
                          padding: '4px 8px'
                        }}
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </SearchableSelect>
                    </div>
                    <div className='d-flex align-items-center'>
                      <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                        Showing <strong>{currentRecords.length > 0 ? ((currentPage - 1) * rowsPerPage) + 1 : 0}-{Math.min(currentPage * rowsPerPage, sortedData.length)}</strong> of <strong>{sortedData.length}</strong>
                      </span>

                      <nav>
                        <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className='page-link text-muted'
                              style={{
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                padding: '8px 12px',
                                fontSize: '14px',
                                borderRadius: '6px'
                              }}
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              ‹
                            </button>
                          </li>

                          {(() => {
                            const pages = []
                            const showPages = 5
                            let startPage = Math.max(1, currentPage - 2)
                            let endPage = Math.min(totalPages, startPage + showPages - 1)

                            if (endPage - startPage < showPages - 1) {
                              startPage = Math.max(1, endPage - showPages + 1)
                            }

                            if (startPage > 1) {
                              pages.push(
                                <li key={1} className='page-item'>
                                  <button
                                    className='page-link text-muted'
                                    style={{
                                      backgroundColor: '#f8f9fa',
                                      border: '1px solid #dee2e6',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                      minWidth: '40px',
                                      borderRadius: '6px'
                                    }}
                                    onClick={() => handlePageChange(1)}
                                  >
                                    1
                                  </button>
                                </li>
                              )

                              if (startPage > 2) {
                                pages.push(
                                  <li key='ellipsis1' className='page-item disabled'>
                                    <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                  </li>
                                )
                              }
                            }

                            for (let i = startPage; i <= endPage; i++) {
                              pages.push(
                                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                  <button
                                    className='page-link text-muted'
                                    style={{
                                      backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                                      border: '1px solid #dee2e6',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                      minWidth: '40px',
                                      borderRadius: '6px',
                                      outline: 'none',
                                      boxShadow: 'none'
                                    }}
                                    onClick={() => handlePageChange(i)}
                                  >
                                    {i}
                                  </button>
                                </li>
                              )
                            }

                            if (endPage < totalPages) {
                              if (endPage < totalPages - 1) {
                                pages.push(
                                  <li key='ellipsis2' className='page-item disabled'>
                                    <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                  </li>
                                )
                              }

                              pages.push(
                                <li key={totalPages} className='page-item'>
                                  <button
                                    className='page-link text-muted'
                                    style={{
                                      backgroundColor: '#f8f9fa',
                                      border: '1px solid #dee2e6',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                      minWidth: '40px',
                                      borderRadius: '6px'
                                    }}
                                    onClick={() => handlePageChange(totalPages)}
                                  >
                                    {totalPages}
                                  </button>
                                </li>
                              )
                            }

                            return pages
                          })()}

                          <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                            <button
                              className='page-link text-muted'
                              style={{
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                padding: '8px 12px',
                                fontSize: '14px',
                                borderRadius: '6px'
                              }}
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages || totalPages === 0}
                            >
                              ›
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
              </div>
          </div>
        </div>
      </div>

      {/* Add In-Port Consumption Modal */}
      <AddInportConsumptionModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onSubmit={handleAddReport}
      />
    </div>
  )
}

export default InportConsumptionReport